/**
 * The whole portal state machine.
 *
 * Five stages: loading, the email gate, the code step, the portal, and a
 * retryable portal load error. Every path back to the gate carries the same
 * neutral wording, because the reasons a session ends are not the customer's
 * business and telling them apart would tell an attacker things too.
 *
 * The gate never branches on what the API answered. Whether the address is one
 * we know or not, the customer sees AUTH_GENERIC_RESPONSE_MESSAGE and moves to
 * the code step. An address we do not hold simply never gets a code that works.
 *
 * Sandbox access rides alongside the portal stage rather than inside it. It is
 * fetched once the documents are on screen, and a failure to fetch it leaves
 * the section hidden and the documents untouched. A revealed secret is handed
 * straight back to the caller and never held here.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  API_ERROR_CODE,
  type ListPortalAccessResponse,
  type ListPortalDocumentsResponse,
  type PortalCustomer,
  type PortalDocument,
} from '@insuredhq/contract';

import * as api from '../api/client';
import { ApiError } from '../api/client';
import {
  AUTH_GENERIC_RESPONSE_MESSAGE,
  GENERIC_FAILURE_MESSAGE,
  PORTAL_LOAD_FAILURE_MESSAGE,
  SESSION_ENDED_MESSAGE,
  VERIFY_FAILED_MESSAGE,
} from '../copy';
import { openPendingTab, type PendingTab } from '../openTab';

export type Stage =
  | { name: 'loading' }
  | { name: 'gate' }
  | { name: 'code'; email: string }
  | { name: 'portal'; data: ListPortalDocumentsResponse }
  | { name: 'portal-error'; customer: PortalCustomer };

/** What a reveal came to. The secret travels in the return value only. */
export type RevealOutcome =
  | { kind: 'revealed'; secret: string; revealedAt: number }
  /** 404: already retrieved, expired, revoked or not theirs. One answer. */
  | { kind: 'unavailable' }
  /** The NDA undertaking has been opened; the caller should return to idle. */
  | { kind: 'nda-required' }
  | { kind: 'session-ended' }
  | { kind: 'failed' };

export interface Portal {
  stage: Stage;
  /** Neutral message shown above the current step, or null. */
  notice: string | null;
  busy: boolean;
  /** Id of the document waiting on an NDA acceptance, or null when closed. */
  ndaFor: string | null;
  /** Id of the document currently being authorised, or null. */
  openingId: string | null;
  /** Id of the credential waiting on an NDA acceptance, or null. */
  ndaForCredential: string | null;
  /** The sandbox access section, or null when not loaded or not loadable. */
  access: ListPortalAccessResponse | null;
  sendCode: (email: string, turnstileToken: string) => Promise<void>;
  verifyCode: (code: string, turnstileToken: string) => Promise<void>;
  returnToGate: () => void;
  retryLoad: () => Promise<void>;
  openDocument: (document: PortalDocument) => Promise<void>;
  acceptNda: () => Promise<void>;
  dismissNda: () => void;
  requestCredentialNda: (credentialId: string) => void;
  revealCredential: (credentialId: string) => Promise<RevealOutcome>;
}

/** The portal slug is the first path segment and nothing else. */
export function readSlug(pathname: string): string | null {
  const [first] = pathname.split('/').filter((segment) => segment.length > 0);
  return first ?? null;
}

export function usePortal(slug: string): Portal {
  const [stage, setStage] = useState<Stage>({ name: 'loading' });
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [ndaFor, setNdaFor] = useState<string | null>(null);
  const [openingId, setOpeningId] = useState<string | null>(null);
  const [ndaForCredential, setNdaForCredential] = useState<string | null>(null);
  const [access, setAccess] = useState<ListPortalAccessResponse | null>(null);
  const emailRef = useRef<string>('');
  const requestIdRef = useRef(0);
  /* Separate from requestIdRef: loading the access section must never cancel
   * a document open or an acceptance, and they must never cancel it. */
  const accessRequestIdRef = useRef(0);

  const isCurrentRequest = useCallback((requestId: number) => {
    return requestIdRef.current === requestId;
  }, []);

  const toGate = useCallback((message: string | null) => {
    requestIdRef.current += 1;
    setBusy(false);
    emailRef.current = '';
    setNdaFor(null);
    setOpeningId(null);
    accessRequestIdRef.current += 1;
    setNdaForCredential(null);
    setAccess(null);
    setStage({ name: 'gate' });
    setNotice(message);
  }, []);

  /** One place decides what a failure means, so every path agrees. */
  const handleFailure = useCallback(
    (error: unknown) => {
      if (error instanceof ApiError && error.isSessionOver) {
        toGate(SESSION_ENDED_MESSAGE);
        return;
      }
      setNotice(GENERIC_FAILURE_MESSAGE);
    },
    [toGate],
  );

  const refresh = useCallback(
    async (requestId?: number): Promise<ListPortalDocumentsResponse> => {
      const data = await api.listDocuments(slug);
      if (requestId === undefined || isCurrentRequest(requestId)) {
        setStage({ name: 'portal', data });
      }
      return data;
    },
    [slug, isCurrentRequest],
  );

  /**
   * A failure keeps whatever was there before. On first load that is nothing,
   * so the section stays hidden; on a reload it is the previous list, so a
   * secret on screen is not unmounted by a transient error.
   */
  const loadAccess = useCallback(async () => {
    const requestId = ++accessRequestIdRef.current;
    try {
      const data = await api.listAccess(slug);
      if (accessRequestIdRef.current === requestId) setAccess(data);
    } catch {
      /* Deliberately swallowed. See the note above. */
    }
  }, [slug]);

  const inPortal = stage.name === 'portal';
  useEffect(() => {
    if (inPortal) void loadAccess();
  }, [inPortal, loadAccess]);

  /* An existing cookie should land the visitor straight in the portal. A first
   * visit lands on the gate with nothing to explain, so no notice here. */
  useEffect(() => {
    let cancelled = false;
    const requestId = ++requestIdRef.current;
    void api
      .listDocuments(slug)
      .then((data) => {
        if (!cancelled && isCurrentRequest(requestId)) setStage({ name: 'portal', data });
      })
      .catch(() => {
        if (!cancelled && isCurrentRequest(requestId)) setStage({ name: 'gate' });
      });
    return () => {
      cancelled = true;
      requestIdRef.current += 1;
    };
  }, [slug, isCurrentRequest]);

  /* Whatever comes back, the customer is told the same thing and moves to the
   * code step. A rejection here would say whether the address is one we hold. */
  const sendCode = useCallback(
    async (email: string, turnstileToken: string) => {
      const requestId = ++requestIdRef.current;
      setBusy(true);
      emailRef.current = email;
      try {
        await api.requestOtp(slug, email, turnstileToken);
      } catch {
        /* Deliberately swallowed. See the note above. */
      } finally {
        if (!isCurrentRequest(requestId)) return;
        setBusy(false);
        setStage({ name: 'code', email });
        setNotice(AUTH_GENERIC_RESPONSE_MESSAGE);
      }
    },
    [slug, isCurrentRequest],
  );

  /* Wrong code, expired code, code already used, address never authorised: one
   * message covers all four, because the differences are the leak. */
  const verifyCode = useCallback(
    async (code: string, turnstileToken: string) => {
      const requestId = ++requestIdRef.current;
      setBusy(true);
      try {
        const verification = await api.verifyOtp(slug, emailRef.current, code, turnstileToken);
        if (!isCurrentRequest(requestId)) return;
        try {
          await refresh(requestId);
          if (!isCurrentRequest(requestId)) return;
        } catch (error) {
          if (!isCurrentRequest(requestId)) return;
          if (error instanceof ApiError && error.isSessionOver) {
            toGate(SESSION_ENDED_MESSAGE);
            return;
          }
          setStage({ name: 'portal-error', customer: verification.customer });
          setNotice(PORTAL_LOAD_FAILURE_MESSAGE);
          return;
        }
        setNotice(null);
      } catch {
        if (!isCurrentRequest(requestId)) return;
        setNotice(VERIFY_FAILED_MESSAGE);
      } finally {
        if (!isCurrentRequest(requestId)) return;
        setBusy(false);
      }
    },
    [slug, refresh, isCurrentRequest, toGate],
  );

  const returnToGate = useCallback(() => {
    toGate(null);
  }, [toGate]);

  const retryLoad = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setBusy(true);
    try {
      await refresh(requestId);
      if (!isCurrentRequest(requestId)) return;
      setNotice(null);
    } catch (error) {
      if (!isCurrentRequest(requestId)) return;
      if (error instanceof ApiError && error.isSessionOver) {
        toGate(SESSION_ENDED_MESSAGE);
      } else {
        setNotice(PORTAL_LOAD_FAILURE_MESSAGE);
      }
    } finally {
      if (isCurrentRequest(requestId)) setBusy(false);
    }
  }, [refresh, isCurrentRequest, toGate]);

  /**
   * @param claimed A tab already opened inside the click that led here. The
   *   acceptance path needs it, because by the time the document is unlocked
   *   the gesture that unlocked it is long over and the browser would refuse a
   *   fresh open.
   */
  const openDocument = useCallback(
    async (document: PortalDocument, claimed?: PendingTab, requestId?: number) => {
      if (document.locked) {
        claimed?.cancel();
        setNdaFor(document.id);
        return;
      }

      const activeRequestId = requestId ?? ++requestIdRef.current;
      setOpeningId(document.id);
      const tab = claimed ?? openPendingTab();
      try {
        const delivery = await api.createDocumentUrl(slug, document.id);
        if (!isCurrentRequest(activeRequestId)) {
          tab.cancel();
          return;
        }
        tab.navigate(delivery.url);
      } catch (error) {
        tab.cancel();
        if (!isCurrentRequest(activeRequestId)) return;
        if (error instanceof ApiError && error.isNdaRequired) {
          setNdaFor(document.id);
          await refresh(activeRequestId).catch((refreshError) => {
            if (isCurrentRequest(activeRequestId)) handleFailure(refreshError);
          });
          return;
        }
        handleFailure(error);
      } finally {
        if (isCurrentRequest(activeRequestId)) setOpeningId(null);
      }
    },
    [slug, refresh, handleFailure, isCurrentRequest],
  );

  /* The credential path opens no tab: the secret is revealed on a second,
   * deliberate click, after the confirmation step, not as a side effect of
   * accepting. */
  const acceptNdaForCredential = useCallback(
    async (ndaVersion: string) => {
      const requestId = ++requestIdRef.current;
      setBusy(true);
      try {
        await api.acceptNda(slug, ndaVersion);
        if (!isCurrentRequest(requestId)) return;
        setAccess((current) => (current === null ? null : { ...current, ndaAccepted: true }));
        await refresh(requestId);
        if (!isCurrentRequest(requestId)) return;
        setNdaForCredential(null);
        void loadAccess();
      } catch (error) {
        if (!isCurrentRequest(requestId)) return;
        setNdaForCredential(null);
        handleFailure(error);
      } finally {
        setBusy(false);
      }
    },
    [slug, refresh, loadAccess, handleFailure, isCurrentRequest],
  );

  const acceptNda = useCallback(async () => {
    if (stage.name !== 'portal') return;
    if (ndaForCredential !== null) {
      await acceptNdaForCredential(stage.data.ndaVersion);
      return;
    }
    if (ndaFor === null) return;
    const pendingId = ndaFor;
    const requestId = ++requestIdRef.current;
    /* Claimed here, while the accept click is still a gesture the browser
     * will honour. Nothing is awaited above this line. */
    const tab = openPendingTab();
    setBusy(true);
    try {
      await api.acceptNda(slug, stage.data.ndaVersion);
      if (!isCurrentRequest(requestId)) {
        tab.cancel();
        return;
      }
      setAccess((current) => (current === null ? null : { ...current, ndaAccepted: true }));
      const data = await refresh(requestId);
      if (!isCurrentRequest(requestId)) {
        tab.cancel();
        return;
      }
      setNdaFor(null);
      const unlocked = data.documents.find((item) => item.id === pendingId);
      if (unlocked && !unlocked.locked) {
        await openDocument(unlocked, tab, requestId);
      } else {
        tab.cancel();
      }
    } catch (error) {
      tab.cancel();
      if (!isCurrentRequest(requestId)) return;
      setNdaFor(null);
      handleFailure(error);
    } finally {
      setBusy(false);
    }
  }, [
    stage,
    ndaFor,
    ndaForCredential,
    slug,
    refresh,
    openDocument,
    acceptNdaForCredential,
    handleFailure,
    isCurrentRequest,
  ]);

  const dismissNda = useCallback(() => {
    if (busy) return;
    requestIdRef.current += 1;
    setNdaFor(null);
    setNdaForCredential(null);
  }, [busy]);

  const requestCredentialNda = useCallback((credentialId: string) => {
    setNdaForCredential(credentialId);
  }, []);

  /**
   * Not tied to requestIdRef. The server destroys the secret as it answers,
   * so a response that arrives is shown, whatever else happened meanwhile.
   */
  const revealCredential = useCallback(
    async (credentialId: string): Promise<RevealOutcome> => {
      try {
        const result = await api.revealCredential(slug, credentialId);
        setAccess((current) =>
          current === null
            ? null
            : {
                ...current,
                credentials: current.credentials.map((credential) =>
                  credential.id === credentialId
                    ? { ...credential, status: 'retrieved', revealedAt: result.revealedAt }
                    : credential,
                ),
              },
        );
        return { kind: 'revealed', secret: result.secret, revealedAt: result.revealedAt };
      } catch (error) {
        if (error instanceof ApiError) {
          if (error.isNdaRequired) {
            setAccess((current) => (current === null ? null : { ...current, ndaAccepted: false }));
            setNdaForCredential(credentialId);
            return { kind: 'nda-required' };
          }
          /* Checked before isSessionOver, which counts not_found as the end
           * of a session. Here it means this credential, not the visitor. */
          if (error.code === API_ERROR_CODE.NOT_FOUND) return { kind: 'unavailable' };
          if (error.isSessionOver) {
            toGate(SESSION_ENDED_MESSAGE);
            return { kind: 'session-ended' };
          }
        }
        return { kind: 'failed' };
      }
    },
    [slug, toGate],
  );

  return {
    stage,
    notice,
    busy,
    ndaFor,
    openingId,
    ndaForCredential,
    access,
    sendCode,
    verifyCode,
    returnToGate,
    retryLoad,
    openDocument,
    acceptNda,
    dismissNda,
    requestCredentialNda,
    revealCredential,
  };
}
